import { alpha, createTheme } from '@mui/material';

/**
 * Tema chiaro con menu scuri (stile WordPress)
 * Conforme WCAG 2.1:
 * - Contrasto testo normale: minimo 4.5:1
 * - Contrasto testo grande (18pt / 14pt bold): minimo 3:1
 * - Contrasto elementi UI: minimo 3:1
 */

/** Marca il paper del drawer di navigazione, l'unico che riceve la palette scura. */
export const NAVIGATION_DRAWER_CLASS = 'app-navigation-drawer';

export const colors = {
  // Colori primari - Viola accessibile
  primary: {
    main: '#6B46C1', // Viola con buon contrasto su bianco (5.6:1)
    light: '#805AD5',
    dark: '#553C9A',
    contrastText: '#FFFFFF',
  },
  // Colori secondari - Teal accessibile
  secondary: {
    main: '#0D9488', // Teal con contrasto 4.5:1 su bianco
    light: '#14B8A6',
    dark: '#0F766E',
    contrastText: '#FFFFFF',
  },
  // Colori di accento
  accent: {
    coral: '#DC2626', // Rosso per icone/bordi/riempimenti (4.8:1 su bianco)
    coralText: '#B91C1C', // Rosso scuro per TESTO errori su tinta 10% (5.5:1)
    amber: '#D97706', // Ambra per icone/bordi/riempimenti (3.1:1 su bianco, non per testo)
    amberText: '#B45309', // Ambra scura per TESTO warning su tinta 10% (4.5:1)
    success: '#059669', // Verde per icone/bordi/riempimenti (3.8:1 su bianco, non per testo)
    successText: '#047857', // Verde scuro per TESTO successo su tinta 10% (4.9:1)
    infoText: '#0277BD', // Blu scuro per i chip info pieni: bianco sopra arriva a 4.8:1
  },
  // Sfondo chiaro per contenuti - adatto ambienti illuminati
  background: {
    default: '#F8FAFC', // Grigio chiarissimo, meno affaticante del bianco puro
    paper: '#FFFFFF',
    elevated: '#F1F5F9',
    subtle: '#E2E8F0',
  },
  // Menu e navigazione scuri (stile WordPress)
  navigation: {
    background: '#1E293B', // Slate scuro
    backgroundHover: '#334155',
    backgroundActive: '#475569',
    text: '#F1F5F9', // Contrasto 12.6:1 su navigation.background
    textMuted: '#94A3B8', // Contrasto 4.6:1 su navigation.background
    border: '#334155',
  },
  // Testo su sfondo chiaro - Alto contrasto WCAG AAA
  text: {
    primary: '#0F172A', // Contrasto 16.1:1 su #FFFFFF
    secondary: '#475569', // Contrasto 7.1:1 su #FFFFFF
    muted: '#64748B', // Contrasto 4.5:1 su #FFFFFF (limite AA)
    disabled: '#94A3B8', // Solo per testo disabilitato
  },
  // Bordi e divisori
  border: {
    light: '#E2E8F0',
    main: '#CBD5E1',
    dark: '#94A3B8',
  },
  // Stati
  states: {
    hover: 'rgba(107, 70, 193, 0.08)',
    selected: 'rgba(107, 70, 193, 0.12)',
    focus: 'rgba(107, 70, 193, 0.24)',
    /** Alone ring visibile dei campi testo (3px), distinto dall'overlay di selezione. */
    focusRing: 'rgba(107, 70, 193, 0.1)',
  },
};

const TOUCH_TARGET_PX = 48;
// Same condition as `useIsMobile`: narrow viewport, or a coarse pointer on a medium one.
const MOBILE_TOUCH_MEDIA_QUERY =
  '@media (max-width:599.95px), (max-width:899.95px) and (pointer: coarse)';

/**
 * Raises the tappable height to 48px through a pseudo-element, keeping the icon and the field layout
 * untouched. It never grows towards the input text or a sibling button: `rightOverflow` only reaches
 * into the field's own end padding.
 */
function touchHitArea(rightOverflow = 0) {
  return {
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: 0,
      right: rightOverflow > 0 ? -rightOverflow : 0,
      height: TOUCH_TARGET_PX,
      transform: 'translateY(-50%)',
    },
  } as const;
}

// End padding of the outlined input that hosts the Autocomplete popup button.
const AUTOCOMPLETE_END_PADDING_PX = 9;

// Shared by menus and autocomplete popups so a dropdown always reads as lifted above the page.
const FLOATING_SURFACE_STYLE = {
  backgroundColor: colors.background.paper,
  border: `1px solid ${colors.border.light}`,
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.12), 0 4px 10px rgba(0, 0, 0, 0.08)',
} as const;

export const aiTheme = createTheme({
  palette: {
    mode: 'light',
    primary: colors.primary,
    secondary: colors.secondary,
    error: {
      main: colors.accent.coral,
      contrastText: '#FFFFFF',
    },
    warning: {
      main: colors.accent.amber,
      contrastText: '#FFFFFF',
    },
    success: {
      main: colors.accent.success,
      contrastText: '#FFFFFF',
    },
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
      disabled: colors.text.disabled,
    },
    divider: colors.border.light,
  },
  typography: {
    fontFamily: '"Outfit Variable", "Inter", system-ui, sans-serif',
    h1: {
      fontFamily: '"Plus Jakarta Sans Variable", "Outfit Variable", system-ui, sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.02em',
      color: colors.text.primary,
    },
    h2: {
      fontFamily: '"Plus Jakarta Sans Variable", "Outfit Variable", system-ui, sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.01em',
      color: colors.text.primary,
    },
    h3: {
      fontFamily: '"Plus Jakarta Sans Variable", "Outfit Variable", system-ui, sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      color: colors.text.primary,
    },
    h4: {
      fontFamily: '"Plus Jakarta Sans Variable", "Outfit Variable", system-ui, sans-serif',
      fontWeight: 600,
      color: colors.text.primary,
    },
    h5: {
      fontFamily: '"Plus Jakarta Sans Variable", "Outfit Variable", system-ui, sans-serif',
      fontWeight: 600,
      color: colors.text.primary,
    },
    h6: {
      fontFamily: '"Plus Jakarta Sans Variable", "Outfit Variable", system-ui, sans-serif',
      fontWeight: 600,
      color: colors.text.primary,
    },
    body1: { lineHeight: 1.7, color: colors.text.primary },
    body2: { lineHeight: 1.6, color: colors.text.secondary },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          '--color-primary': colors.primary.main,
          '--color-secondary': colors.secondary.main,
          '--color-background': colors.background.default,
          '--color-surface': colors.background.paper,
          '--color-nav-bg': colors.navigation.background,
        },
        body: {
          backgroundColor: colors.background.default,
          // Ancorato al viewport (fixed + no-repeat): agganciato al box del body
          // il gradiente si dimensionava sull'altezza/larghezza del documento e
          // il bordo dell'ellisse si vedeva come uno stacco netto chiaro/grigio.
          backgroundImage: 'radial-gradient(ellipse at 50% 0%, #F1F5F9 0%, #F8FAFC 70%)',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          scrollbarWidth: 'thin',
          scrollbarColor: `${colors.border.main} ${colors.background.default}`,
          '&::-webkit-scrollbar': {
            width: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: colors.background.default,
          },
          '&::-webkit-scrollbar-thumb': {
            background: colors.border.main,
            borderRadius: 4,
            '&:hover': {
              background: colors.border.dark,
            },
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 10,
          padding: '10px 20px',
          variants: [
            {
              props: { variant: 'contained' },
              style: {
                boxShadow: 'none',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  boxShadow: 'none',
                },
              },
            },
            {
              props: { variant: 'contained', color: 'primary' },
              style: {
                background: 'linear-gradient(135deg, #6B46C1, #805AD5)',
                backgroundColor: colors.primary.main,
                color: colors.primary.contrastText,
                '&:hover': {
                  background: 'linear-gradient(135deg, #805AD5, #6B46C1)',
                  backgroundColor: colors.primary.dark,
                },
                /* `background: none` alone stripped the gradient *and* MUI's
                   disabled surface, so a disabled primary action rendered as
                   bare grey text and stopped reading as a button at all. */
                '&.Mui-disabled': {
                  background: colors.background.subtle,
                  color: colors.text.muted,
                },
              },
            },
            {
              props: { variant: 'outlined' },
              style: {
                border: 'none',
                color: colors.text.primary,
                backgroundColor: colors.background.elevated,
                '&:hover': {
                  border: 'none',
                  backgroundColor: colors.states.hover,
                },
              },
            },
            {
              props: { variant: 'outlined', color: 'primary' },
              style: {
                color: colors.primary.main,
                backgroundColor: alpha(colors.primary.main, 0.08),
                '&:hover': {
                  backgroundColor: alpha(colors.primary.main, 0.14),
                },
              },
            },
            {
              props: { variant: 'outlined', color: 'warning' },
              style: {
                color: colors.accent.amberText,
                backgroundColor: alpha(colors.accent.amber, 0.08),
                '&:hover': {
                  // alpha 0.10: sopra il testo scuro non raggiunge 4.5:1
                  backgroundColor: alpha(colors.accent.amber, 0.1),
                },
              },
            },
            {
              props: { variant: 'outlined', color: 'error' },
              style: {
                color: colors.accent.coralText,
                backgroundColor: alpha(colors.accent.coral, 0.08),
                '&:hover': {
                  backgroundColor: alpha(colors.accent.coral, 0.14),
                },
              },
            },
            /**
             * Il text button è comunque un controllo: senza un contorno proprio
             * si legge come una scritta in grassetto e su una superficie tinta
             * (banner, alert, riga selezionata) sparisce del tutto. Qui prende
             * una "pillola fantasma": superficie propria + bordo sottile, meno
             * peso di `outlined` ma sempre riconoscibile.
             */
            {
              props: { variant: 'text' },
              style: {
                color: colors.text.primary,
                backgroundColor: colors.background.paper,
                border: `1px solid ${colors.border.light}`,
                '&:hover': {
                  backgroundColor: colors.background.elevated,
                  borderColor: colors.border.main,
                },
                '&.Mui-disabled': {
                  borderColor: colors.border.light,
                  backgroundColor: 'transparent',
                },
              },
            },
            {
              // `inherit` è usato dentro gli Alert: il testo segue la severità
              // della superficie, la pillola bianca lo stacca dal fondo tinto.
              props: { variant: 'text', color: 'inherit' },
              style: {
                color: 'inherit',
              },
            },
            {
              props: { variant: 'text', color: 'primary' },
              style: {
                color: colors.primary.main,
                borderColor: alpha(colors.primary.main, 0.32),
                '&:hover': {
                  backgroundColor: alpha(colors.primary.main, 0.08),
                  borderColor: alpha(colors.primary.main, 0.5),
                },
              },
            },
            {
              props: { variant: 'text', color: 'warning' },
              style: {
                color: colors.accent.amberText,
                borderColor: alpha(colors.accent.amber, 0.4),
                '&:hover': {
                  backgroundColor: alpha(colors.accent.amber, 0.1),
                  borderColor: alpha(colors.accent.amber, 0.6),
                },
              },
            },
            {
              props: { variant: 'text', color: 'error' },
              style: {
                color: colors.accent.coralText,
                borderColor: alpha(colors.accent.coral, 0.32),
                '&:hover': {
                  backgroundColor: alpha(colors.accent.coral, 0.08),
                  borderColor: alpha(colors.accent.coral, 0.5),
                },
              },
            },
            {
              props: { variant: 'text', color: 'success' },
              style: {
                color: colors.accent.successText,
                borderColor: alpha(colors.accent.success, 0.32),
                '&:hover': {
                  backgroundColor: alpha(colors.accent.success, 0.08),
                  borderColor: alpha(colors.accent.success, 0.5),
                },
              },
            },
          ],
        },
        sizeLarge: {
          padding: '14px 28px',
          fontSize: '1rem',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: colors.background.paper,
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        },
        elevation2: {
          boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
        },
        elevation3: {
          boxShadow: '0 12px 24px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
          border: `1px solid ${colors.border.light}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'none',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.navigation.background,
          color: colors.navigation.text,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        // Solo il drawer di navigazione è scuro: gli altri drawer restano su
        // background.paper, altrimenti il testo del tema (scuro) ci finisce sopra.
        paper: {
          [`&.${NAVIGATION_DRAWER_CLASS}`]: {
            backgroundColor: colors.navigation.background,
            color: colors.navigation.text,
            borderRight: `1px solid ${colors.navigation.border}`,
          },
          [`&.${NAVIGATION_DRAWER_CLASS} .MuiListItemButton-root`]: {
            '& .MuiListItemText-primary': {
              color: '#E2E8F0', // Contrasto 9.7:1 su #1E293B
            },
            '& .MuiListItemIcon-root': {
              color: '#E2E8F0', // Contrasto 9.7:1 su #1E293B
            },
            '&:hover': {
              backgroundColor: colors.navigation.backgroundHover,
              '& .MuiListItemText-primary': {
                color: '#F8FAFC',
              },
              '& .MuiListItemIcon-root': {
                color: '#F8FAFC',
              },
            },
            '&.Mui-selected': {
              backgroundColor: colors.navigation.backgroundHover,
              '& .MuiListItemText-primary': {
                color: '#FFFFFF', // Contrasto 11.7:1 - WCAG AAA
                fontWeight: 600,
              },
              '& .MuiListItemIcon-root': {
                color: '#FFFFFF', // Contrasto 11.7:1 - WCAG AAA
              },
              '&:hover': {
                backgroundColor: colors.navigation.backgroundActive,
              },
            },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          transition: 'color 0.2s ease-in-out, background-color 0.2s ease-in-out',
          '& .MuiListItemText-primary': {
            color: colors.text.primary, // Contrasto 16.1:1 su #FFFFFF
          },
          '& .MuiListItemIcon-root': {
            color: colors.text.secondary, // Contrasto 7.1:1 su #FFFFFF
          },
          '&:hover': {
            backgroundColor: colors.states.hover,
          },
          '&.Mui-selected': {
            backgroundColor: colors.states.selected,
            '& .MuiListItemText-primary': {
              fontWeight: 600,
            },
            '&:hover': {
              backgroundColor: colors.states.selected,
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 40,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontWeight: 500,
          fontSize: '0.9rem',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: colors.background.paper,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.border.light,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.border.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary.main,
              borderWidth: 2,
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${colors.states.focusRing}`,
            },
          },
          '& .MuiInputLabel-root': {
            color: colors.text.secondary,
            '&.Mui-focused': {
              color: colors.primary.main,
            },
          },
          '& .MuiInputBase-input': {
            color: colors.text.primary,
          },
          '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active':
            {
              WebkitBoxShadow: `0 0 0 30px ${colors.background.paper} inset !important`,
              WebkitTextFillColor: `${colors.text.primary} !important`,
              caretColor: colors.text.primary,
            },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontWeight: 500,
          height: 36,
          transition: 'transform 0.1s ease, background-color 0.5s ease-in-out',
          '&:active': {
            transform: 'scale(0.97)',
          },
          // Scoped to `color: 'default'`: these overrides are injected after
          // MUI's own per-colour variants and win on equal specificity, so an
          // unscoped rule repainted every `color="error|warning|info|success"`
          // chip neutral grey — the status colour legend silently died.
          variants: [
            {
              props: { variant: 'filled', color: 'default' },
              style: {
                backgroundColor: colors.background.elevated,
                color: colors.text.primary,
                '&:hover': {
                  backgroundColor: colors.background.subtle,
                },
              },
            },
            {
              props: { variant: 'filled', color: 'primary' },
              style: {
                backgroundColor: colors.primary.main,
                color: colors.primary.contrastText,
              },
            },
            {
              // Chip stato filled: il main (600) con testo bianco non arriva a
              // 4.5:1 (warning 3.19, success 3.77) → sfondo a livello 700.
              props: { variant: 'filled', color: 'warning' },
              style: {
                backgroundColor: colors.accent.amberText,
                color: '#FFFFFF',
              },
            },
            {
              props: { variant: 'filled', color: 'success' },
              style: {
                backgroundColor: colors.accent.successText,
                color: '#FFFFFF',
              },
            },
            {
              // Il blu info di MUI (#0288D1) con testo bianco si ferma a 3.9:1.
              props: { variant: 'filled', color: 'info' },
              style: {
                backgroundColor: colors.accent.infoText,
                color: '#FFFFFF',
              },
            },
            {
              props: { variant: 'outlined', color: 'default' },
              style: {
                borderColor: colors.border.main,
                color: colors.text.primary,
              },
            },
          ],
        },
        label: {
          padding: '0 14px',
        },
        // The 14px label padding above is measured for a bare chip. MUI's
        // default icon margins (4px in, -4px out) leave an icon-bearing chip
        // glued to its left border with a 10px hole before the text; these
        // margins restore a 10px inset and a 6px icon-to-label gap.
        icon: {
          marginLeft: 10,
          marginRight: -8,
        },
        deleteIcon: {
          marginLeft: -8,
          marginRight: 10,
        },
        sizeSmall: {
          height: 30,
          '& .MuiChip-label': {
            padding: '0 10px',
          },
          '& .MuiChip-icon': {
            marginLeft: 8,
            marginRight: -5,
          },
          '& .MuiChip-deleteIcon': {
            marginLeft: -5,
            marginRight: 8,
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: colors.primary.main,
            outlineOffset: 2,
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          textRendering: 'optimizeLegibility',
          WebkitFontSmoothing: 'antialiased',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: 10,
          borderRadius: 8,
          '& .MuiSvgIcon-root': {
            fontSize: '1.4rem',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: 10,
          borderRadius: 8,
          '& .MuiSvgIcon-root': {
            fontSize: '1.4rem',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          color: colors.text.secondary,
          '&:hover': {
            backgroundColor: colors.states.hover,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popupIndicator: {
          [MOBILE_TOUCH_MEDIA_QUERY]: touchHitArea(AUTOCOMPLETE_END_PADDING_PX),
        },
        clearIndicator: {
          [MOBILE_TOUCH_MEDIA_QUERY]: touchHitArea(),
        },
        paper: {
          ...FLOATING_SURFACE_STYLE,
          borderRadius: 12,
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          [MOBILE_TOUCH_MEDIA_QUERY]: {
            '& .MuiIconButton-root': touchHitArea(),
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: -8,
          '& .MuiFormControlLabel-label': {
            fontSize: '0.95rem',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.navigation.background,
          color: colors.navigation.text,
          fontSize: '0.8rem',
          fontWeight: 500,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: FLOATING_SURFACE_STYLE,
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: colors.text.primary,
          '&:hover': {
            backgroundColor: colors.states.hover,
          },
          '&.Mui-selected': {
            backgroundColor: colors.states.selected,
            '&:hover': {
              backgroundColor: colors.states.selected,
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.border.light,
          },
        },
        paperFullScreen: {
          paddingTop: 'env(safe-area-inset-top, 0px)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.border.light,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: colors.primary.main,
          textDecorationColor: alpha(colors.primary.main, 0.4),
          '&:hover': {
            color: colors.primary.dark,
          },
        },
      },
    },
    MuiAlert: {
      // Per-severity colors follow the HACCP legend. The base `standard` variant
      // slot must NOT set a color: MUI applies the variant slot after the color
      // slot, so a colored `standard` rule silently overrode every `color*` rule
      // and made all alerts purple regardless of severity. Color lives only in
      // the color-scoped slots below.
      styleOverrides: {
        // Il body text usa il token scuro per-passata: sulla tinta 10% il colore
        // vivo non raggiunge 4.5:1 (warning 2.86, error 4.13, success 3.35).
        // Icona e bordo restano sul colore vivo via override espliciti.
        colorError: {
          backgroundColor: alpha(colors.accent.coral, 0.1),
          color: colors.accent.coralText,
          '& .MuiAlert-icon': {
            color: colors.accent.coral,
          },
        },
        colorWarning: {
          backgroundColor: alpha(colors.accent.amber, 0.1),
          color: colors.accent.amberText,
          '& .MuiAlert-icon': {
            color: colors.accent.amber,
          },
        },
        colorSuccess: {
          backgroundColor: alpha(colors.accent.success, 0.1),
          color: colors.accent.successText,
          '& .MuiAlert-icon': {
            color: colors.accent.success,
          },
        },
        colorInfo: {
          backgroundColor: alpha(colors.primary.main, 0.1),
          color: colors.primary.main,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: colors.border.light,
        },
        head: {
          backgroundColor: colors.background.elevated,
          color: colors.text.primary,
          fontWeight: 600,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: colors.primary.main,
            '& + .MuiSwitch-track': {
              backgroundColor: colors.primary.main,
            },
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: { minHeight: 44, minWidth: 44 },
        sizeSmall: { minHeight: 40, minWidth: 40, padding: '6px 12px' },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        rectangular: { borderRadius: 8 },
        rounded: { borderRadius: 12 },
      },
    },
  },
});
